/**
 * On-device spell-check dictionary (Australian English).
 *
 * Loads an en-AU Hunspell dictionary into `nspell` entirely in the browser — no
 * report text ever leaves the device (PHI-safe). The dictionary files are
 * vendored under `public/dict/en-au/` (source: the `dictionary-en-au` package,
 * MIT AND BSD; see that folder's LICENSE) and fetched lazily on first use so the
 * ~540 KB payload isn't in the initial editor chunk.
 */
import nspell from "nspell";

export type SpellChecker = {
  /** True if the word is spelled correctly (or explicitly ignored). */
  isCorrect: (word: string) => boolean;
  /** Ranked correction suggestions for a misspelled word. */
  suggest: (word: string) => string[];
  /** Ignore a word for the rest of the session (adds to the dictionary). */
  ignore: (word: string) => void;
};

const AFF_URL = "/dict/en-au/en-au.aff";
const DIC_URL = "/dict/en-au/en-au.dic";

// Medicolegal terms an en-AU dictionary commonly flags but that are valid here.
// Extend as testers surface false positives (kept lowercase; match is
// case-insensitive).
const ALLOWLIST = new Set(
  [
    "medicolegal",
    "impairment",
    "claimant",
    "referrer",
    "telehealth",
    "comorbidity",
    "comorbidities",
    "biopsychosocial",
    "musculoskeletal",
    "radiculopathy",
    "whiplash",
    "prognosis",
    "aetiology",
    "sequelae",
    "workcover",
    "suremed",
    "medirect",
  ].map((w) => w.toLowerCase())
);

let loadPromise: Promise<SpellChecker | null> | null = null;

async function load(): Promise<SpellChecker | null> {
  try {
    const [affRes, dicRes] = await Promise.all([fetch(AFF_URL), fetch(DIC_URL)]);
    if (!affRes.ok || !dicRes.ok) {
      throw new Error(`dictionary fetch failed: ${affRes.status}/${dicRes.status}`);
    }
    const [aff, dic] = await Promise.all([affRes.text(), dicRes.text()]);
    // A dev/SPA server answers a missing asset with 200 + the index.html shell
    // instead of a 404, so `res.ok` passes but the body is HTML. A real Hunspell
    // .aff starts with directives (e.g. `SET UTF-8`); bail if we got markup.
    if (aff.trimStart().startsWith("<") || dic.trimStart().startsWith("<")) {
      throw new Error("dictionary fetch returned HTML (missing vendored files?)");
    }
    const spell = nspell(aff, dic);

    const ignored = new Set<string>();
    return {
      isCorrect: (word) => {
        const w = word.trim();
        if (!w) return true;
        const lower = w.toLowerCase();
        if (ALLOWLIST.has(lower) || ignored.has(lower)) return true;
        return spell.correct(w);
      },
      suggest: (word) => spell.suggest(word).slice(0, 6),
      ignore: (word) => {
        ignored.add(word.trim().toLowerCase());
        spell.add(word.trim());
      },
    };
  } catch (err) {
    // Fail soft: no dictionary → no squiggles, editor keeps working.
    console.error("Spell-check dictionary failed to load:", err);
    return null;
  }
}

/** Lazily load (once) and return the shared en-AU spell checker, or null. */
export function getSpellChecker(): Promise<SpellChecker | null> {
  if (!loadPromise) loadPromise = load();
  return loadPromise;
}
